import { useCallback, useMemo, useState } from 'react';
import {
  MappingResponse,
  TransformationTestResponse,
  apiService,
} from '../../services/api';
import { loadPreviewSamples } from '../../utils/preview';

export type TablePreviewTarget = {
  tableKey?: string;
  label: string;
  role?: 'source' | 'target';
  sheetId?: string;
  candidates?: string[];
};

type TableNameVariants = {
  normalized: string;
  base: string;
  leaf: string;
  slug: string;
  baseSlug: string;
  leafSlug: string;
};

const normaliseTableName = (value: string): string =>
  (value || '').replace(/["'`]/g, '').trim().toLowerCase();

const stripTableExtension = (value: string): string =>
  value.replace(/\.(csv|tsv|json|parquet|txt)$/i, '');

const slugifyTableName = (value: string): string => value.replace(/[^a-z0-9]/g, '');

const buildTableNameVariants = (raw?: string | null): TableNameVariants | null => {
  if (!raw) return null;
  const normalized = normaliseTableName(raw);
  if (!normalized) return null;
  const base = stripTableExtension(normalized);
  const leaf = normalized.split('.').filter(Boolean).pop() ?? normalized;
  const baseLeaf = base.split('.').filter(Boolean).pop() ?? base;
  return {
    normalized,
    base,
    leaf,
    slug: slugifyTableName(normalized),
    baseSlug: slugifyTableName(base),
    leafSlug: slugifyTableName(baseLeaf || leaf),
  };
};

const tableNamesRoughlyMatch = (
  left: TableNameVariants | null,
  right: TableNameVariants | null,
): boolean => {
  if (!left || !right) return false;
  const comparisons: Array<[string, string]> = [
    [left.normalized, right.normalized],
    [left.base, right.base],
    [left.leaf, right.leaf],
    [left.slug, right.slug],
    [left.baseSlug, right.baseSlug],
    [left.leafSlug, right.leafSlug],
  ];
  return comparisons.some(
    ([candidate, entry]) =>
      candidate.length > 0 && entry.length > 0 && candidate === entry,
  );
};

export const derivePreviewTableCandidates = (
  target?: TablePreviewTarget | null,
): string[] => {
  if (!target) return [];
  if (Array.isArray(target.candidates) && target.candidates.length > 0)
    return target.candidates;
  return [target.tableKey, target.label]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value && value.length > 0));
};

export const extractSampleOutputForTables = (
  sampleOutput: TransformationTestResponse['sample_output'] | undefined,
  candidates?: string[],
): TransformationTestResponse['sample_output'] => {
  if (!sampleOutput || !candidates || candidates.length === 0) return {};
  const preparedCandidates = Array.from(
    new Set(
      candidates
        .map((c) => c?.trim())
        .filter((v): v is string => Boolean(v && v.length > 0)),
    ),
  )
    .map((value) => ({ value, variants: buildTableNameVariants(value) }))
    .filter((entry) => Boolean(entry.variants));
  if (!preparedCandidates.length) return {};
  const entries = Object.entries(sampleOutput).filter(([tableName]) => {
    const entryVariants = buildTableNameVariants(tableName);
    if (!entryVariants) return false;
    return preparedCandidates.some((candidate) =>
      tableNamesRoughlyMatch(candidate.variants, entryVariants),
    );
  });
  if (!entries.length) return {};
  return Object.fromEntries(entries);
};

const sampleOutputHasRows = (
  sampleOutput?: TransformationTestResponse['sample_output'] | null,
): boolean =>
  Object.values(sampleOutput || {}).some(
    (rows) => Array.isArray(rows) && rows.length > 0,
  );

export interface UseTablePreviewOptions {
  mappingResponse?: MappingResponse;
  activeWorkbookId?: string;
  recordCanvasEvent: (kind: string, payload: Record<string, string>) => void;
  /** Called after a preview completes successfully — use to invalidate health query */
  onPreviewComplete?: () => void;
}

export function useTablePreview({
  mappingResponse,
  activeWorkbookId,
  recordCanvasEvent,
  onPreviewComplete,
}: UseTablePreviewOptions) {
  const [tablePreviewOpen, setTablePreviewOpen] = useState(false);
  const [tablePreviewTarget, setTablePreviewTarget] =
    useState<TablePreviewTarget | null>(null);
  const [tablePreviewLoading, setTablePreviewLoading] = useState(false);
  const [tablePreviewError, setTablePreviewError] = useState<string | null>(null);
  const [tablePreviewNotice, setTablePreviewNotice] = useState<string | null>(null);
  const [tablePreviewResponse, setTablePreviewResponse] =
    useState<TransformationTestResponse | null>(null);

  const handleOpenTablePreview = useCallback(
    async (target: TablePreviewTarget) => {
      setTablePreviewTarget(target);
      setTablePreviewOpen(true);
      setTablePreviewError(null);
      setTablePreviewNotice(null);
      setTablePreviewResponse(null);

      if (!mappingResponse?.batch_id || !mappingResponse?.recipe_id) {
        setTablePreviewError('Recipe context missing; unable to load preview.');
        return;
      }

      setTablePreviewLoading(true);
      recordCanvasEvent('canvas.preview.table_opened', {
        batch_id: mappingResponse.batch_id,
        workbook_id: activeWorkbookId ?? '',
        table: target.tableKey ?? target.label,
        role: target.role ?? 'unknown',
      });
      try {
        const previewTargets = derivePreviewTableCandidates(target);
        const result = await loadPreviewSamples(
          mappingResponse.batch_id,
          mappingResponse.recipe_id,
          40,
          previewTargets,
          undefined,
        );
        setTablePreviewResponse(result.response);
        setTablePreviewNotice(result.notice ?? null);
        // Preview may have updated preview_warnings in metadata — refresh health
        onPreviewComplete?.();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to load preview.';
        setTablePreviewError(message);
      } finally {
        setTablePreviewLoading(false);
      }
    },
    [mappingResponse, recordCanvasEvent, activeWorkbookId, onPreviewComplete],
  );

  const handleCloseTablePreview = useCallback(() => {
    setTablePreviewOpen(false);
    setTablePreviewLoading(false);
    setTablePreviewError(null);
  }, []);

  const tablePreviewSampleOutput = useMemo(() => {
    if (!tablePreviewResponse?.sample_output) return {};
    const candidateList = derivePreviewTableCandidates(tablePreviewTarget);
    return extractSampleOutputForTables(
      tablePreviewResponse.sample_output,
      candidateList,
    );
  }, [tablePreviewResponse, tablePreviewTarget]);

  const tablePreviewHasSampleRows = useMemo(
    () => sampleOutputHasRows(tablePreviewSampleOutput),
    [tablePreviewSampleOutput],
  );

  return {
    tablePreviewOpen,
    tablePreviewTarget,
    tablePreviewLoading,
    tablePreviewError,
    tablePreviewNotice,
    tablePreviewResponse,
    tablePreviewSampleOutput,
    tablePreviewHasSampleRows,
    handleOpenTablePreview,
    handleCloseTablePreview,
  };
}
